import React, { useMemo, useState } from 'react'
import { formatAmount } from '../../utils/chemicalCalculations'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot
} from 'recharts'
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material'
import {
  CHART_PARAMETERS,
  Y_AXIS_CONFIG,
  formatChartValue,
  getVisibleParameters,
  calculateChartDomain,
  getTargetRangeBands,
  CHEMICAL_CATEGORIES,
  getChemicalIcon
} from '../../utils/chartUtils'

// Custom component for chemical addition icons
const ChemicalIcon = ({ cx, cy, chemicalName, category }) => {
  const letter = getChemicalIcon(chemicalName)
  const categoryColor = CHEMICAL_CATEGORIES[category]?.color || '#9E9E9E'
  
  return (
    <g>
      <circle 
        cx={cx} 
        cy={cy} 
        r={12} 
        fill={categoryColor}
        stroke="#fff"
        strokeWidth={2}
        opacity={0.9}
      />
      <text 
        x={cx} 
        y={cy} 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize={10}
        fontWeight="bold"
        fill="#fff"
      >
        {letter}
      </text>
    </g>
  )
}

const HistoryChart = ({ 
  data, 
  chemicalAdditions = [], 
  showChemicals = false,
  poolConfig = null,
  loading = false 
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [hiddenParameters, setHiddenParameters] = useState(
    isMobile ? ['water_temperature', 'combined_chlorine', 'calcium_hardness'] : []
  )
  const [showSettings, setShowSettings] = useState(false)

  // Get visible parameters based on screen size and user preferences
  const visibleParameters = useMemo(() => 
    getVisibleParameters(isMobile, hiddenParameters)
  , [isMobile, hiddenParameters])

  // Calculate target ranges for reference lines
  const targetRanges = useMemo(() => {
    if (!poolConfig || !data.length) return {}
    const latestMeasurement = data[data.length - 1]
    return getTargetRangeBands(poolConfig, latestMeasurement)
  }, [poolConfig, data])

  // Calculate domains for each y-axis
  const domains = useMemo(() => {
    const result = {}
    Object.keys(Y_AXIS_CONFIG).forEach(yAxisId => {
      result[yAxisId] = calculateChartDomain(data, yAxisId)
    })
    return result
  }, [data])

  // Prepare chemical additions with positioning for multiple icons per day
  const positionedChemicalAdditions = useMemo(() => {
    if (!showChemicals || !chemicalAdditions.length) return []
    
    // Group chemicals by day
    const groupedByDay = {}
    chemicalAdditions.forEach(addition => {
      const dayKey = new Date(addition.timestamp).toDateString()
      if (!groupedByDay[dayKey]) {
        groupedByDay[dayKey] = []
      }
      groupedByDay[dayKey].push(addition)
    })
    
    // Position icons with offset for multiple per day
    const positioned = []
    Object.values(groupedByDay).forEach(dayAdditions => {
      dayAdditions.forEach((addition, index) => {
        const offsetX = index * 30 * 60 * 1000 // 30 minutes offset in milliseconds
        positioned.push({
          ...addition,
          displayTimestamp: addition.timestamp + offsetX,
          originalTimestamp: addition.timestamp,
          isMultiple: dayAdditions.length > 1,
          dayIndex: index
        })
      })
    })
    
    return positioned
  }, [chemicalAdditions, showChemicals])

  // Get active y-axes based on visible parameters
  const activeYAxes = useMemo(() => {
    const axes = new Set()
    visibleParameters.forEach(param => {
      const config = CHART_PARAMETERS[param]
      if (config) axes.add(config.yAxisId)
    })
    let axesArray = Array.from(axes)
    
    // Ensure we always have at least one y-axis
    if (axesArray.length === 0) {
      axesArray.push('chlorine')
    }
    
    // Ensure 'chlorine' is always first (default Y-axis)
    if (axesArray.includes('chlorine')) {
      axesArray = ['chlorine', ...axesArray.filter(axis => axis !== 'chlorine')]
    }
    
    return axesArray
  }, [visibleParameters])

  const toggleParameter = (paramKey) => {
    setHiddenParameters(prev => 
      prev.includes(paramKey) 
        ? prev.filter(p => p !== paramKey)
        : [...prev, paramKey]
    )
  }

  const formatXAxisTick = (tickItem) => {
    const date = new Date(tickItem)
    if (isMobile) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? '2-digit' : undefined
    })
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null

    const date = new Date(label)
    const chemicalAdditionsForDay = chemicalAdditions.filter(addition => 
      Math.abs(addition.timestamp - label) < 24 * 60 * 60 * 1000 // Within 24 hours
    )

    return (
      <Card sx={{ p: 2, maxWidth: 300, boxShadow: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {date.toLocaleDateString('en-US', { 
            weekday: 'short',
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </Typography>
        
        <Stack spacing={0.5}>
          {payload.map((entry) => {
            const config = CHART_PARAMETERS[entry.dataKey]
            if (!config || !visibleParameters.includes(entry.dataKey)) return null
            
            return (
              <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 2, 
                    backgroundColor: entry.color,
                    borderRadius: 1
                  }} 
                />
                <Typography variant="body2">
                  <strong>{config.label}:</strong> {formatChartValue(entry.value, entry.dataKey)}
                </Typography>
              </Box>
            )
          })}
        </Stack>

        {showChemicals && chemicalAdditionsForDay.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              Chemical{chemicalAdditionsForDay.length > 1 ? 's' : ''} Added:
            </Typography>
            {chemicalAdditionsForDay.map((addition, index) => (
              <Typography key={addition.id} variant="body2" sx={{ mt: 0.5 }}>
                {addition.displayName || addition.chemical_types?.name || 'Unknown'} - {formatAmount(addition.amount, addition.unit)}
              </Typography>
            ))}
          </Box>
        )}
      </Card>
    )
  }

  const CustomLegend = (props) => {
    const { payload } = props
    if (!payload) return null

    return (
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1, 
        justifyContent: 'center',
        mt: 2
      }}>
        {payload.map((entry) => {
          const config = CHART_PARAMETERS[entry.dataKey]
          if (!config) return null
          
          const isVisible = visibleParameters.includes(entry.dataKey)
          
          return (
            <Chip
              key={entry.dataKey}
              label={isMobile ? config.shortLabel : config.label}
              size="small"
              variant={isVisible ? 'filled' : 'outlined'}
              sx={{
                backgroundColor: isVisible ? entry.color : 'transparent',
                color: isVisible ? 'white' : entry.color,
                borderColor: entry.color,
                '&:hover': {
                  backgroundColor: isVisible ? entry.color : 'rgba(0,0,0,0.04)'
                }
              }}
              onClick={() => toggleParameter(entry.dataKey)}
            />
          )
        })}
      </Box>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading chart data...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add some measurements to see trends over time.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        {/* Chart Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
            Chemistry Trends
          </Typography>
          
          <IconButton 
            onClick={() => setShowSettings(!showSettings)}
            sx={{ p: 1 }}
          >
            {showSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Chart Settings */}
        <Collapse in={showSettings}>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Chart Options
            </Typography>
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Visible Parameters
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(CHART_PARAMETERS).map(([key, config]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={!hiddenParameters.includes(key)}
                          onChange={() => toggleParameter(key)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {isMobile ? config.shortLabel : config.label}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
        </Collapse>

        {/* Chart */}
        <Box sx={{ width: '100%', height: isMobile ? 350 : 450 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{
                top: 20,
                right: isMobile ? 15 : 80,
                bottom: isMobile ? 60 : 20,
                left: isMobile ? 15 : 40
              }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={theme.palette.divider}
                opacity={0.5}
              />
              
              <XAxis
                dataKey="date"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatXAxisTick}
                stroke={theme.palette.text.secondary}
                fontSize={12}
                interval="preserveStartEnd"
              />

              {/* Y-Axes */}
              {activeYAxes.map((yAxisId, index) => {
                const config = Y_AXIS_CONFIG[yAxisId]
                const domain = domains[yAxisId]
                
                return (
                  <YAxis
                    key={yAxisId}
                    yAxisId={yAxisId === 'chlorine' ? undefined : yAxisId}
                    orientation={yAxisId === 'chlorine' ? 'left' : 'right'}
                    domain={domain}
                    label={{ 
                      value: config.label, 
                      angle: yAxisId === 'chlorine' ? -90 : 90, 
                      position: yAxisId === 'chlorine' ? 'insideLeft' : 'insideRight'
                    }}
                    stroke={config.color}
                    fontSize={11}
                    width={isMobile ? 35 : 50}
                  />
                )
              })}

              {/* Target Range Reference Lines */}
              {Object.entries(targetRanges).map(([param, range]) => {
                if (!visibleParameters.includes(param) || !range.ideal) return null
                const config = CHART_PARAMETERS[param]
                if (!config || !activeYAxes.includes(config.yAxisId)) return null
                
                const yAxisId = config.yAxisId === 'chlorine' ? undefined : config.yAxisId
                
                return (
                  <ReferenceLine
                    key={`ideal-${param}`}
                    y={range.ideal}
                    yAxisId={yAxisId}
                    stroke={config.color}
                    strokeDasharray="8 8"
                    opacity={0.6}
                  />
                )
              })}

              {/* Parameter Lines */}
              {visibleParameters.map(paramKey => {
                const config = CHART_PARAMETERS[paramKey]
                if (!config || !activeYAxes.includes(config.yAxisId)) return null
                
                const yAxisId = config.yAxisId === 'chlorine' ? undefined : config.yAxisId
                
                return (
                  <Line
                    key={paramKey}
                    type="monotone"
                    dataKey={paramKey}
                    yAxisId={yAxisId}
                    stroke={config.color}
                    strokeWidth={config.strokeWidth}
                    dot={config.dot}
                    connectNulls={false}
                    activeDot={{ r: 5, stroke: config.color, strokeWidth: 2 }}
                  />
                )
              })}

              {/* Chemical Addition Icons */}
              {showChemicals && activeYAxes.length > 0 && positionedChemicalAdditions.map(addition => {
                // Use the first active Y-axis to ensure ReferenceDot has a valid coordinate system
                const primaryYAxis = activeYAxes[0]
                const yAxisId = primaryYAxis === 'chlorine' ? undefined : primaryYAxis
                
                return (
                  <ReferenceDot
                    key={addition.id}
                    x={addition.displayTimestamp}
                    y={domains[primaryYAxis]?.[0] || 0}
                    yAxisId={yAxisId}
                    r={12}
                    fill={CHEMICAL_CATEGORIES[addition.chemical_types?.category || 'other']?.color || '#9E9E9E'}
                    stroke="#fff"
                    strokeWidth={2}
                    shape={(props) => (
                      <ChemicalIcon
                        cx={props.cx}
                        cy={props.cy}
                        chemicalName={addition.chemical_types?.name || addition.displayName || 'Unknown'}
                        category={addition.chemical_types?.category || 'other'}
                      />
                    )}
                  />
                )
              })}

              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  )
}

export default HistoryChart